import { LineChart, X } from "lucide-react";
import React from "react";

import {
  IgIconButton,
  IgPurpleGradientButton,
  IgTypography,
} from "@intelligems/ig-theme";
import { Box, Stack, SxProps, Theme, useTheme } from "@mui/material";
import { Sparkle } from "@src/images/Icons/Sparkle";

import { RecommendationAction, RecommendationCardProps } from "./types";

const Actions = ({ actions }: { actions: RecommendationAction[] }) => {
  const theme = useTheme();

  return (
    <Stack direction="row" spacing={1} flexWrap="nowrap" useFlexGap>
      {actions.map((action) => {
        const isSingleAction = actions.length === 1;
        const isSecondary = action.tone === "secondary" && !isSingleAction;

        return (
          <IgPurpleGradientButton
            key={action.key}
            size="extraSmall"
            variant={isSecondary ? "outlined" : "contained"}
            color="primary"
            loading={action.loading}
            disabled={action.disabled}
            onClick={action.onClick}
            sx={
              isSecondary
                ? {
                    minHeight: "36px !important",
                    height: "36px",
                    borderRadius: "6px",
                    px: "12px",
                    py: "8px",
                    fontSize: "14px",
                    lineHeight: "21px",
                    fontWeight: 600,
                    textTransform: "none",
                    letterSpacing: "0px",
                    whiteSpace: "nowrap",
                    color: theme.palette.text.primary,
                    borderColor: theme.igBorders.core.default,
                    backgroundColor: "transparent",
                    "&:hover": {
                      borderColor: theme.igBorders.core.default,
                      backgroundColor: theme.igPrimitives.gray["300"],
                    },
                  }
                : undefined
            }
          >
            {action.label}
          </IgPurpleGradientButton>
        );
      })}
    </Stack>
  );
};

export const RecommendationCard = ({
  actions = [],
  headline,
  supportingText,
  insight,
  onDismiss,
  label,
  sx,
  actionsPosition,
}: RecommendationCardProps) => {
  const theme = useTheme();

  return (
    <Stack
      direction="column"
      spacing={3}
      sx={
        [
          {
            width: "100%",
            borderRadius: "14px",
            border: `1px solid ${theme.igPrimitives.purple[800]}`,
            backgroundColor: theme.igPrimitives.purple[100],
            padding: "16px 24px",
          },
          sx || {},
        ] as SxProps<Theme>
      }
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
      >
        <Stack
          direction="row"
          spacing="8px"
          alignItems="center"
          sx={{ minWidth: 0, flex: 1 }}
        >
          <Sparkle variant="contained" />
          <IgTypography
            variant="body2"
            sx={{
              color: theme.palette.text.primary,
              fontSize: "12px",
              lineHeight: "18px",
              fontWeight: 600,
              letterSpacing: "0px",
            }}
          >
            {label}
          </IgTypography>
        </Stack>
        <Stack direction={"row"} alignItems="center" spacing={2}>
          {actionsPosition === "top" && actions.length > 0 ? (
            <Actions actions={actions} />
          ) : null}
          {onDismiss ? (
            <IgIconButton
              size="small"
              onClick={onDismiss}
              sx={{
                width: "16px",
                height: "16px",
                padding: "0px",
                "&:hover": { backgroundColor: "transparent" },
              }}
            >
              <X size={16} color={theme.palette.text.secondary} />
            </IgIconButton>
          ) : null}
        </Stack>
      </Stack>

      <IgTypography
        variant="body2"
        sx={{
          color: theme.palette.text.primary,
          fontWeight: 600,
          fontSize: "16px",
          lineHeight: "24px",
          letterSpacing: "0px",
        }}
      >
        {headline}
      </IgTypography>

      {supportingText ? (
        <IgTypography
          variant="body2"
          sx={{
            color: theme.palette.text.primary,
            fontSize: "12px",
            lineHeight: "18px",
            letterSpacing: "0px",
          }}
        >
          {supportingText}
        </IgTypography>
      ) : null}

      {insight ? (
        <Box
          sx={{
            borderRadius: "5.021px",
            padding: "12px",
            background:
              "linear-gradient(177.117deg, rgba(152, 16, 250, 0.15) 0%, rgba(21, 93, 252, 0.15) 100%)",
          }}
        >
          <Stack direction="row" alignItems="flex-start" spacing="4px">
            <Stack direction="row" alignItems="center" spacing="4px">
              <LineChart size={16} />
              <IgTypography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: "12px",
                  lineHeight: "18px",
                  fontWeight: 600,
                  letterSpacing: "0px",
                }}
              >
                Proven impact:
              </IgTypography>
            </Stack>
            <IgTypography
              variant="body2"
              sx={{
                color: theme.palette.text.primary,
                fontSize: "12px",
                lineHeight: "18px",
                fontWeight: 400,
                letterSpacing: "0px",
                minWidth: 0,
                flex: 1,
              }}
            >
              {insight}
            </IgTypography>
          </Stack>
        </Box>
      ) : null}
      {actionsPosition === "bottom" && actions.length > 0 ? (
        <Actions actions={actions} />
      ) : null}
    </Stack>
  );
};
